import { inject } from '@angular/core';
import { Dialog } from '@capacitor/dialog';
import type {
  DialogAdapter,
  ExternalUrlAdapter,
  PhotoSourceAdapter,
  QrScanAdapter,
} from '@nebalkon-workspace/cabinet-core';
import {
  CABINET_APP_INFO,
  CABINET_APP_INFO_LOADER,
  CABINET_DIALOG,
  CABINET_EXTERNAL_URL,
  CABINET_LEGAL_URLS,
  CABINET_PATHS,
  CABINET_PHOTO_SOURCE,
  CABINET_PLATFORM,
  CABINET_QR_SCAN,
  MOBILE_CABINET_PATHS,
} from '@nebalkon-workspace/cabinet-core';
import {
  LEGAL_ACCOUNT_DELETION_URL,
  LEGAL_HUB_URL,
  LEGAL_PRIVACY_POLICY_URL,
  LEGAL_PUBLIC_OFFER_URL,
  LEGAL_USER_AGREEMENT_URL,
} from '../constants/legal-site-urls';
import { loadMobileCabinetAppInfo } from './mobile-cabinet-app-info';
import { environment } from '@nebalkon-workspace/environments';
import { PaymentBrowserService } from '../../services/payment-browser.service';
import { CameraService } from '../services/camera.service';
import { QrScanService } from '../services/qr-scan.service';
import {
  canOpenExternalUrl,
  openExternalUrl,
} from '../utils/open-external-url';

const mobileDialogAdapter: DialogAdapter = {
  confirm: async ({
    title,
    message,
    okLabel,
    cancelLabel,
    warningsTitle,
    warnings,
  }) => {
    const warningBlock =
      warnings?.length && warnings.length > 0
        ? `\n\n${warningsTitle ? `${warningsTitle}\n` : ''}${warnings.map((item) => `• ${item}`).join('\n')}`
        : '';

    const { value } = await Dialog.confirm({
      title,
      message: `${message}${warningBlock}`,
      okButtonTitle: okLabel,
      cancelButtonTitle: cancelLabel,
    });
    return value;
  },
  prompt: async ({
    title,
    message,
    inputPlaceholder,
    inputValue,
    okLabel,
    cancelLabel,
  }) => {
    const result = await Dialog.prompt({
      title,
      message,
      okButtonTitle: okLabel,
      cancelButtonTitle: cancelLabel,
      inputPlaceholder,
      inputText: inputValue,
    });
    return { cancelled: result.cancelled, value: result.value ?? '' };
  },
  alert: async ({ title, message, okLabel }) => {
    await Dialog.alert({ title, message, buttonTitle: okLabel });
  },
};

function createMobileQrScanAdapter(qrScan: QrScanService): QrScanAdapter {
  return {
    scanQrCode: () => qrScan.scanQrCode(),
  };
}

function createMobileExternalUrlAdapter(
  paymentBrowser: PaymentBrowserService,
): ExternalUrlAdapter {
  return {
    canOpenUrl: canOpenExternalUrl,
    openUrl: async (url, mode, options): Promise<void> => {
      if (mode === 'redirect') {
        await paymentBrowser.openPaymentUrl(url, options?.intentId);
        return;
      }

      await openExternalUrl(url);
    },
  };
}

function createMobilePhotoSourceAdapter(camera: CameraService): PhotoSourceAdapter {
  return {
    pickPhotos: async (): Promise<File[]> => {
      const outcome = await camera.selectPhoto();
      if (outcome.kind === 'success') {
        return [outcome.photo.file];
      }
      return [];
    },
  };
}

export const mobileCabinetPlatformProviders = [
  { provide: CABINET_PATHS, useValue: MOBILE_CABINET_PATHS },
  { provide: CABINET_PLATFORM, useValue: 'mobile' as const },
  {
    provide: CABINET_APP_INFO,
    useValue: { buildLabel: environment.cabinetBuildLabel },
  },
  { provide: CABINET_APP_INFO_LOADER, useValue: loadMobileCabinetAppInfo },
  { provide: CABINET_DIALOG, useValue: mobileDialogAdapter },
  {
    provide: CABINET_QR_SCAN,
    useFactory: (): QrScanAdapter => createMobileQrScanAdapter(inject(QrScanService)),
  },
  {
    provide: CABINET_PHOTO_SOURCE,
    useFactory: (): PhotoSourceAdapter =>
      createMobilePhotoSourceAdapter(inject(CameraService)),
  },
  {
    provide: CABINET_EXTERNAL_URL,
    useFactory: (): ExternalUrlAdapter =>
      createMobileExternalUrlAdapter(inject(PaymentBrowserService)),
  },
  {
    provide: CABINET_LEGAL_URLS,
    useValue: {
      hub: LEGAL_HUB_URL,
      privacyPolicy: LEGAL_PRIVACY_POLICY_URL,
      userAgreement: LEGAL_USER_AGREEMENT_URL,
      publicOffer: LEGAL_PUBLIC_OFFER_URL,
      accountDeletion: LEGAL_ACCOUNT_DELETION_URL,
    },
  },
];
